import React, { useState, useEffect, useCallback } from 'react';
import { app, db } from '../../../firebaseConfig';
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  writeBatch,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import SideNavbar from '../../../components/SideNavbar';
import { useAppSelector } from '../../../redux/store';
import Toast from '../../../components/Toast';
import * as XLSX from 'xlsx';

interface Column {
  name: string;
  sample: string[];
  data: string[];
}

interface ColumnMapping {
  name: string | null;
  number: string | null;
  email: string | null;
  source: string | null;
  campaign: string | null;
  adminNotes: string | null;
  stage: string | null;
  state: string | null;
  country: string | null;
  leadStatus: string | null;
  followUpDate: string | null;
  createdAt: string | null;
}

interface LeadSource {
  id: string;
  channel: string;
  source: string;
}

interface LeadCampaign {
  id: string;
  campaign: string;
}

interface LeadStage {
  id: string;
  stage: string;
  color: string;
  priority: number;
}

interface LeadState {
  id: string;
  state: string;
  color: string;
  makeInactive: boolean;
}

interface LeadStatuses {
  id: string;
  status: string;
}

interface Countries {
  id: string;
  name: string;
}

interface RowMapping {
  [excelValue: string]: string; // maps excel value to source ID
}

interface StaffMember {
  uid: string;
  name: string;
  email: string;
  phone: string;
  isStaff: boolean;
  isActive: boolean;
}

interface StaffDistribution {
  staffId: string;
  percentage: number;
}

interface Lead {
  name: string;
  number: string;
  reversedNumber: string;
  email: string;
  sourceId: string;
  createdBy: string;
  isActive: boolean;
  campaignId: string;
  assignedTo: string;
  adminNotes: string;
  stageId: string;
  stateId: string;
  destinationId: string;
  statusId: string;
  hasActiveFollowUp: boolean;
  createdAt: ReturnType<typeof serverTimestamp>;
  updatedAt: ReturnType<typeof serverTimestamp>;
}

interface ValidationIssue {
  type: 'duplicate' | 'missing_number' | 'firestore_duplicate';
  rows: Array<{ name: string; row: number }>;
  number?: string;
}

const REQUIRED_FIELDS = {
  name: {
    label: 'Student Name',
    description: 'Full name of the student',
    required: true,
  },
  number: {
    label: 'Phone Number',
    description: 'Contact number with country code',
    required: true,
  },
  source: {
    label: 'Lead Source',
    description: 'Where the lead came from (e.g., Facebook, Instagram)',
    required: true,
  },
  email: {
    label: 'Email Address',
    description: 'Student email address',
    required: false,
  },
  campaign: {
    label: 'Campaign Name',
    description: 'Marketing campaign that generated this lead',
    required: false,
  },
  adminNotes: {
    label: 'Admin Notes',
    description: 'Administrative notes for this lead',
    required: false,
  },
  stage: {
    label: 'Lead Stage',
    description: 'Current stage of the lead',
    required: false,
  },
  state: {
    label: 'Lead State',
    description: 'Current state of the lead',
    required: false,
  },
  country: {
    label: 'Destination Country',
    description: 'Destination country for the lead',
    required: false,
  },
  leadStatus: {
    label: 'Lead Status',
    description: 'Current status of the lead',
    required: false,
  },
  followUpDate: {
    label: 'Follow-up Date',
    description: 'Date for the next follow-up with the lead',
    required: false,
  },
  createdAt: {
    label: 'Created Date',
    description: 'Date when the lead was created',
    required: false,
  },
};

const MigrationBeta: React.FC = () => {
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [columns, setColumns] = useState<Column[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({
    name: null,
    number: null,
    email: null,
    source: null,
    campaign: null,
    adminNotes: null,
    stage: null,
    state: null,
    country: null,
    leadStatus: null,
    followUpDate: null,
    createdAt: null,
  });
  const [leadSources, setLeadSources] = useState<LeadSource[]>([]);
  const [leadCampaigns, setLeadCampaigns] = useState<LeadCampaign[]>([]);
  const [leadStages, setLeadStages] = useState<LeadStage[]>([]);
  const [leadStates, setLeadStates] = useState<LeadState[]>([]);
  const [countries, setCountries] = useState<Countries[]>([]);
  const [leadStatuses, setLeadStatuses] = useState<LeadStatuses[]>([]);
  const [sourceMapping, setSourceMapping] = useState<RowMapping>({});
  const [campaignMapping, setCampaignMapping] = useState<RowMapping>({});
  const [stageMapping, setStageMapping] = useState<RowMapping>({});
  const [stateMapping, setStateMapping] = useState<RowMapping>({});
  const [countryMapping, setCountryMapping] = useState<RowMapping>({});
  const [leadStatusMapping, setLeadStatusMapping] = useState<RowMapping>({});
  const [uniqueSourceValues, setUniqueSourceValues] = useState<string[]>([]);
  const [uniqueCampaignValues, setUniqueCampaignValues] = useState<string[]>([]);
  const [uniqueStageValues, setUniqueStageValues] = useState<string[]>([]);
  const [uniqueStateValues, setUniqueStateValues] = useState<string[]>([]);
  const [uniqueCountryValues, setUniqueCountryValues] = useState<string[]>([]);
  const [uniqueLeadStatusValues, setUniqueLeadStatusValues] = useState<string[]>([]);
  const [showRowMapping, setShowRowMapping] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [invalidFollowUpDates, setInvalidFollowUpDates] = useState<{ row: number; value: string }[]>([]);
  const [invalidCreatedDates, setInvalidCreatedDates] = useState<{ row: number; value: string }[]>([]);
  const [showInvalidDatesPopup, setShowInvalidDatesPopup] = useState(false);
  const [invalidDateType, setInvalidDateType] = useState<'followUp' | 'created' | 'both'>('followUp');
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [staffDistribution, setStaffDistribution] = useState<StaffDistribution[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  const [duplicateCheckProgress, setDuplicateCheckProgress] = useState({ checked: 0, total: 0 });
  const [duplicateNumbers, setDuplicateNumbers] = useState<Array<{ number: string; rows: { name: string; row: number }[] }>>([]);
  const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>([]);
  const [firestoreDuplicates, setFirestoreDuplicates] = useState<ValidationIssue[]>([]);
  const [exportUniqueOnly, setExportUniqueOnly] = useState(false);
  const auth = getAuth(app);
  const isCollapsed = useAppSelector((state) => state.sidebar.isCollapsed);

  const tabs = [
    { id: 'source', label: 'Map Sources', icon: 'user-group' },
    { id: 'campaign', label: 'Map Campaigns', icon: 'document-text' },
    { id: 'stage', label: 'Map Stages', icon: 'flag' },
    { id: 'state', label: 'Map States', icon: 'status-online' },
    { id: 'country', label: 'Map Countries', icon: 'globe' },
    { id: 'leadStatus', label: 'Map Lead Status', icon: 'status-online' }
  ];
  const [activeTab, setActiveTab] = useState('source');

  // Fetch company ID and initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        if (!auth.currentUser) {
          setError('No authenticated user found');
          return;
        }

        const userDocRef = doc(db, 'users', auth.currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCompanyId(userData.companyId);
        }

        setIsLoading(false);
      } catch (err) {
        setError('Error fetching initial data');
        console.error('Error:', err);
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [auth.currentUser]);

  useEffect(() => {
    fetchRowMappingOptions();
  }, []);

  useEffect(() => {
    if (showExport) {
      fetchStaffMembers();
    }
  }, [showExport]);

  const fetchRowMappingOptions = async () => {
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      // Get companyId from user document
      const userDoc = await getDoc(doc(db, 'users', uid));
      const companyId = userDoc.data()?.companyId;

      if (!companyId) {
        console.error('No company ID found for user');
        showToastMessage('Error: No company ID found');
        return;
      }

      const leadSourcesRef = collection(db, 'companies', companyId, 'leadSources');
      const leadCampaignsRef = collection(db, 'companies', companyId, 'leadCampaigns');
      const leadStagesRef = collection(db, 'companies', companyId, 'leadStages');
      const leadStatesRef = collection(db, 'companies', companyId, 'leadStates');
      const leadStatusesRef = collection(db, 'companies', companyId, 'leadStatuses');
      const countriesRef = collection(db, 'countries');

      const leadSourcesSnap = await getDocs(leadSourcesRef);
      const sources = leadSourcesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as LeadSource[];

      const leadCampaignsSnap = await getDocs(leadCampaignsRef);
      const campaigns = leadCampaignsSnap.docs.filter(doc => doc.data().isActive).map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as LeadCampaign[];

      const leadStagesSnap = await getDocs(leadStagesRef);
      const stages = leadStagesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as LeadStage[];

      const leadStatesSnap = await getDocs(leadStatesRef);
      const states = leadStatesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as LeadState[];

      const leadStatusesSnap = await getDocs(leadStatusesRef);
      const statuses = leadStatusesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as LeadStatuses[];

      const countriesSnap = await getDocs(countriesRef);
      const countryList = countriesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Countries[];

      setLeadSources(sources);
      setLeadCampaigns(campaigns);
      setLeadStages(stages);
      setLeadStates(states);
      setLeadStatuses(statuses);
      setCountries(countryList);
    } catch (error) {
      console.error('Error fetching lead sources:', error);
      showToastMessage('Error fetching lead sources');
    }
  };

  const fetchStaffMembers = async () => {
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      // Get companyId from user document
      const userDoc = await getDoc(doc(db, 'users', uid));
      const companyId = userDoc.data()?.companyId;

      if (!companyId) {
        console.error('No company ID found for user');
        showToastMessage('Error: No company ID found');
        return;
      }

      // Get all staff members for the company
      const usersRef = collection(db, 'users');
      const q = query(usersRef,
        where('companyId', '==', companyId),
        where('isStaff', '==', true),
        where('isActive', '==', true)
      );

      const staffSnap = await getDocs(q);
      const staff = staffSnap.docs.map(doc => ({
        uid: doc.id,
        ...(doc.data() as Omit<StaffMember, 'uid'>)
      }));

      setStaffMembers(staff);
    } catch (error) {
      console.error('Error fetching staff members:', error);
      showToastMessage('Error fetching staff members');
    }
  };

  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const isEmptyOrSpecialChars = (value: string): boolean => {
    // If value is empty or undefined
    if (!value || value.trim() === '') return true;

    // Regular expression to match only special characters
    // This regex matches any character that is NOT a letter, number, or space
    const specialCharsRegex = /^[^a-zA-Z0-9\s]*$/;
    return specialCharsRegex.test(value.trim());
  };

  const isRowEmptyOrSpecial = (row: string[]): boolean => {
    // If row is empty or undefined
    if (!row || row.length === 0) return true;

    // Check if all cells in the row are either empty or contain only special characters
    return row.every(cell => isEmptyOrSpecialChars(String(cell)));
  };

  const processExcelFile = async (file: File) => {
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as string[][];

      if (jsonData.length < 2) {
        throw new Error('Excel file must contain at least a header row and one data row');
      }

      // Filter out empty rows or rows with only special characters
      const validRows = jsonData.filter((row, index) => {
        const isEmptyRow = isRowEmptyOrSpecial(row);
        if (isEmptyRow) {
          console.log(`Row ${index + 1} is empty or contains only special characters`);
        }
        return !isEmptyRow;
      });

      const headers = validRows[0];
      const cols: Column[] = headers.map((header, colIndex) => ({
        name: header,
        sample: validRows.slice(1, 6).map(row => row[colIndex] || ''),  // First 5 rows for sample
        data: validRows.slice(1).map(row => row[colIndex] || '')  // All rows except header
      }));

      setColumns(cols);
      setFile(file);
      showToastMessage('File uploaded successfully!');
    } catch (error) {
      console.error('Error reading Excel file:', error);
      showToastMessage('Error reading Excel file');
    }
  };

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls)$/)) {
      showToastMessage('Please upload a valid Excel file (.xlsx or .xls)');
      return;
    }

    processExcelFile(file);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length) {
      const file = files[0];
      if (!file.name.match(/\.(xlsx|xls)$/)) {
        showToastMessage('Please upload a valid Excel file (.xlsx or .xls)');
        return;
      }
      processExcelFile(file);
    }
  }, []);

  const handleColumnMap = (field: keyof ColumnMapping, columnName: string | null) => {
    setColumnMapping(prev => ({
      ...prev,
      [field]: columnName
    }));
  };

  const canProceedToNextStep = () => {
    return Object.entries(REQUIRED_FIELDS).every(([field, config]) => {
      if (config.required) {
        return columnMapping[field as keyof ColumnMapping] !== null;
      }
      return true;
    });
  };

  const getAvailableColumns = (currentField: keyof ColumnMapping) => {
    const usedColumns = Object.entries(columnMapping)
      .filter(([field, value]) => field !== currentField && value !== null)
      .map(([_, value]) => value);

    return columns.filter(column => !usedColumns.includes(column.name));
  };

  const extractUniqueSourceValues = () => {
    if (!columns || !columnMapping.source) return;

    const sourceColumn = columns.find(col => col.name === columnMapping.source);
    if (!sourceColumn) return;

    // Get all values from the source column
    const uniqueValues = Array.from(new Set(sourceColumn.data));
    setUniqueSourceValues(uniqueValues);
  };

  const extractUniqueCampaignValues = () => {
    if (!columns || !columnMapping.campaign) return;

    const campaignColumn = columns.find(col => col.name === columnMapping.campaign);
    if (!campaignColumn) return;

    // Get all values from the campaign column
    const uniqueValues = Array.from(new Set(campaignColumn.data));
    setUniqueCampaignValues(uniqueValues);
  };

  const extractUniqueStageValues = () => {
    if (!columns || !columnMapping.stage) return;

    const stageColumn = columns.find(col => col.name === columnMapping.stage);
    if (!stageColumn) return;

    // Get all values from the stage column
    const uniqueValues = Array.from(new Set(stageColumn.data));
    setUniqueStageValues(uniqueValues);
  };

  const extractUniqueStateValues = () => {
    if (!columns || !columnMapping.state) return;

    const stateColumn = columns.find(col => col.name === columnMapping.state);
    if (!stateColumn) return;

    // Get all values from the state column
    const uniqueValues = Array.from(new Set(stateColumn.data));
    setUniqueStateValues(uniqueValues);
  };

  const extractUniqueCountryValues = () => {
    if (!columns || !columnMapping.country) return;

    const countryColumn = columns.find(col => col.name === columnMapping.country);
    if (!countryColumn) return;

    // Get all values from the country column
    const uniqueValues = Array.from(new Set(countryColumn.data));
    setUniqueCountryValues(uniqueValues);
  };

  const extractUniqueLeadStatusValues = () => {
    if (!columns || !columnMapping.leadStatus) return;

    const leadStatusColumn = columns.find(col => col.name === columnMapping.leadStatus);
    if (!leadStatusColumn) return;

    // Get all values from the leadStatus column
    const uniqueValues = Array.from(new Set(leadStatusColumn.data));
    setUniqueLeadStatusValues(uniqueValues);
  };

  useEffect(() => {
    if (showRowMapping) {
      extractUniqueSourceValues();
      extractUniqueCampaignValues();
      extractUniqueStageValues();
      extractUniqueStateValues();
      extractUniqueCountryValues();
      extractUniqueLeadStatusValues();
    }
  }, [showRowMapping]);

  const handleSourceMap = (value: string, sourceId: string) => {
    setSourceMapping(prev => ({
      ...prev,
      [value]: sourceId
    }));
  };

  const handleProceedToExport = () => {
    // Check if all required mappings are complete
    const isSourceMappingComplete = uniqueSourceValues.every(value => sourceMapping[value]);
    const isCampaignMappingComplete = uniqueCampaignValues.every(value => campaignMapping[value]);
    const isStageMappingComplete = uniqueStageValues.every(value => stageMapping[value]);
    const isStateMappingComplete = uniqueStateValues.every(value => stateMapping[value]);

    // Country and leadStatus are optional, so we only check if they have values in the column mapping
    const isCountryMappingComplete = !columnMapping.country || uniqueCountryValues.every(value => countryMapping[value]);
    const isLeadStatusMappingComplete = !columnMapping.leadStatus || uniqueLeadStatusValues.every(value => leadStatusMapping[value]);

    if (!isSourceMappingComplete || !isCampaignMappingComplete || !isStageMappingComplete || !isStateMappingComplete ||
      !isCountryMappingComplete || !isLeadStatusMappingComplete) {
      showToastMessage('Please complete all mappings before proceeding');
      return;
    }

    // Proceed to export
    setShowExport(true);
    showToastMessage('Moving to export phase...');
  };

  const handleCampaignMap = (excelValue: string, campaignId: string) => {
    setCampaignMapping(prev => ({
      ...prev,
      [excelValue]: campaignId
    }));
  };

  const handleStageMap = (excelValue: string, stageId: string) => {
    setStageMapping(prev => ({
      ...prev,
      [excelValue]: stageId
    }));
  };

  const handleStateMap = (excelValue: string, stateId: string) => {
    setStateMapping(prev => ({
      ...prev,
      [excelValue]: stateId
    }));
  };

  const handleCountryMap = (excelValue: string, countryId: string) => {
    setCountryMapping(prev => ({
      ...prev,
      [excelValue]: countryId
    }));
  };

  const handleLeadStatusMap = (excelValue: string, statusId: string) => {
    setLeadStatusMapping(prev => ({
      ...prev,
      [excelValue]: statusId
    }));
  };

  // DISCLAIMER: Phone numbers with 10 or fewer digits will be formatted with +91 prefix (for 'number') 
  // and 19+ suffix (for 'reversedNumber'). Only numbers longer than 10 digits follow the previous formatting logic.
  // Format phone number consistently
  const formatPhoneNumber = (
    phoneNumber: string,
    source: 'excel' | 'firestore' = 'excel' // Default to 'excel' for backward compatibility
  ): string => {
    const cleanNumber = phoneNumber.replace(/\D/g, '');

    // If the source is Firestore, we assume it's already formatted or just needs cleaning.
    // We just ensure it has a '+' prefix.
    if (source === 'firestore') {
      return cleanNumber.startsWith('+') ? cleanNumber : `+${cleanNumber}`;
    }

    // For Excel source, apply the existing logic
    if (cleanNumber.length > 10) {
      return cleanNumber.startsWith('+') ? cleanNumber : `+${cleanNumber}`;
    }

    return `+91${cleanNumber}`;
  };

  const validateFollowUpDate = (dateValue: any): boolean => {
    if (dateValue === null || dateValue === undefined) return true; // Empty dates are valid (will be ignored)

    try {
      // Convert to string if it's not already
      const dateString = String(dateValue).trim();
      if (dateString === '') return true; // Empty string is valid (will be ignored)

      // Check if it's an Excel serial date (numeric string that's a valid number)
      const numericValue = Number(dateString);
      if (!isNaN(numericValue) && numericValue > 0) {
        // Convert Excel serial date to JavaScript Date
        // Excel dates are days since 1900-01-01, but with a quirk: Excel incorrectly assumes 1900 is a leap year
        // So we need to adjust for dates after February 28, 1900
        const excelDate = new Date(Math.round((numericValue - (numericValue > 60 ? 2 : 1)) * 86400000) + new Date(1900, 0, 1).getTime());
        return !isNaN(excelDate.getTime());
      }

      // Common date formats regex patterns
      const datePatterns = [
        // DD/MM/YYYY or DD-MM-YYYY
        /^(0?[1-9]|[12][0-9]|3[01])[\/\-](0?[1-9]|1[0-2])[\/\-](\d{4})$/,
        // MM/DD/YYYY or MM-DD-YYYY
        /^(0?[1-9]|1[0-2])[\/\-](0?[1-9]|[12][0-9]|3[01])[\/\-](\d{4})$/,
        // YYYY/MM/DD or YYYY-MM-DD
        /^(\d{4})[\/\-](0?[1-9]|1[0-2])[\/\-](0?[1-9]|[12][0-9]|3[01])$/,
        // Month DD, YYYY (e.g., January 21, 2025)
        /^(January|February|March|April|May|June|July|August|September|October|November|December)\s+(0?[1-9]|[12][0-9]|3[01]),\s+(\d{4})$/i,
        // DD Month YYYY (e.g., 21 January 2025)
        /^(0?[1-9]|[12][0-9]|3[01])\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})$/i,
      ];

      // Check if the date string matches any of the patterns
      const isValidFormat = datePatterns.some(pattern => pattern.test(dateString));
      if (!isValidFormat) return false;

      // Try to parse the date
      const date = new Date(dateString);
      return !isNaN(date.getTime());
    } catch (error) {
      return false;
    }
  };

  const validateAllFollowUpDates = () => {
    if (!columnMapping.followUpDate) return true; // No follow-up date column mapped

    const followUpDateColumn = columns.find(col => col.name === columnMapping.followUpDate);
    if (!followUpDateColumn) return true; // Column not found

    const invalidDates: { row: number; value: string }[] = [];

    // Check each row for invalid dates
    followUpDateColumn.data.forEach((value, index) => {
      if (value && !validateFollowUpDate(value)) {
        invalidDates.push({ row: index + 1, value: String(value) });
      }
    });

    setInvalidFollowUpDates(invalidDates);
    return invalidDates.length === 0;
  };

  const validateAllCreatedDates = () => {
    if (!columnMapping.createdAt) return true; // No created date column mapped

    const createdAtColumn = columns.find(col => col.name === columnMapping.createdAt);
    if (!createdAtColumn) return true; // Column not found

    const invalidDates: { row: number; value: string }[] = [];

    // Check each row for invalid dates
    createdAtColumn.data.forEach((value, index) => {
      if (value && !validateFollowUpDate(value)) { // Reuse the same validation function
        invalidDates.push({ row: index + 1, value: String(value) });
      }
    });

    setInvalidCreatedDates(invalidDates);
    return invalidDates.length === 0;
  };

  const checkDuplicateNumbers = useCallback(() => {
    const numberColumn = columns.find(col => col.name === columnMapping.number);
    if (!numberColumn) return;

    const numberMap = new Map<string, number[]>();
    numberColumn.data.forEach((num, index) => {
      const formattedNumber = formatPhoneNumber(String(num || ''));
      if (formattedNumber) {
        const indices = numberMap.get(formattedNumber) || [];
        indices.push(index);
        numberMap.set(formattedNumber, indices);
      }
    });

    const duplicates = Array.from(numberMap.entries())
      .filter(([_, indices]) => indices.length > 1)
      .map(([number, indices]) => ({
        number,
        rows: indices.map(index => ({
          name: columns.find(col => col.name === columnMapping.name)?.data[index] || '',
          row: index + 2 // Adding 2 because index 0 is row 2 (after header)
        }))
      }));

    setDuplicateNumbers(duplicates);
  }, [columns, columnMapping.number, columnMapping.name]);

  useEffect(() => {
    if (showExport) {
      checkDuplicateNumbers();
    }
  }, [showExport, checkDuplicateNumbers]);

  const checkFirestoreDuplicates = async (numbers: string[]): Promise<ValidationIssue[]> => {
    setIsCheckingDuplicates(true);
    setDuplicateCheckProgress({ checked: 0, total: numbers.length });
    try {
      const issues: ValidationIssue[] = [];
      const batches = [];

      // Create batches of 30 numbers
      for (let i = 0; i < numbers.length; i += 30) {
        batches.push(numbers.slice(i, i + 30));
      }

      // Get companyId
      const uid = auth.currentUser?.uid;
      if (!uid) return [];
      const userDoc = await getDoc(doc(db, 'users', uid));
      const companyId = userDoc.data()?.companyId;
      if (!companyId) return [];

      // Check each batch
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        const startIndex = i * 30;
        setDuplicateCheckProgress(prev => ({
          ...prev,
          checked: Math.min(startIndex + 30, numbers.length)
        }));
        const reversedNumbers = batch.map(num => {
          const formattedNumber = formatPhoneNumber(String(num || ''));
          // Ensure number is valid before reversing
          let reversedNumber = formattedNumber.replace(/\D/g, '').split('').reverse().join('');

          // Add + at end of reversed number if longer than 10 chars and doesn't already have +
          if (!reversedNumber.endsWith('+')) {
            reversedNumber = reversedNumber + '+';
          }
          return reversedNumber;
        });

        const q = query(
          collection(db, 'companies', companyId, 'leads'),
          where('reversedNumber', 'in', reversedNumbers)
        );

        const snapshot = await getDocs(q);
        snapshot.forEach(doc => {
          const lead = doc.data();
          const originalNumber = lead.number;
          const index = numbers.findIndex(
            (num) =>
              formatPhoneNumber(String(num || ''), 'excel') ===
              formatPhoneNumber(originalNumber, 'firestore')
          );

          if (index !== -1) {
            const rawValue = columns.find(col => col.name === columnMapping.name)?.data[index];
            const name = typeof rawValue === 'string'
              ? rawValue.trim()
              : String(rawValue || '').trim();

            issues.push({
              type: 'firestore_duplicate',
              rows: [{
                name: name || 'NO NAME',
                row: index + 2
              }],
              number: originalNumber
            });
          }
        });
      }

      return issues;
    } catch (error) {
      showToastMessage('Error checking for duplicate numbers');
      console.error('Error checking Firestore duplicates:', error);
      return [];
    } finally {
      setIsCheckingDuplicates(false);
      setDuplicateCheckProgress({ checked: 0, total: 0 });
    }
  };

  // Function to determine if there are validation issues that should block export
  const hasBlockingValidationIssues = () => {
    if (validationIssues.length === 0) return false;

    // If exporting unique only, we should only block for missing numbers
    if (exportUniqueOnly) {
      return validationIssues.some(issue => issue.type === 'missing_number');
    }

    // Otherwise, block for all validation issues
    return validationIssues.length > 0;
  };

  const checkValidation = useCallback(async () => {
    const issues: ValidationIssue[] = [];

    // Check for missing numbers
    const numberColumn = columns.find(col => col.name === columnMapping.number);
    const nameColumn = columns.find(col => col.name === columnMapping.name);
    if (!numberColumn || !nameColumn) return;

    const missingNumbers: { rows: Array<{ name: string; row: number }> } = {
      rows: []
    };

    numberColumn.data.forEach((num, index) => {
      const cleanNumber = String(num || '').replace(/\D/g, '');
      if (!cleanNumber) {
        const name = nameColumn.data[index].trim() || 'NO NAME';
        missingNumbers.rows.push({
          name,
          row: index + 2 // Adding 2 because index 0 is row 2 (after header)
        });
      }
    });

    if (missingNumbers.rows.length > 0) {
      issues.push({
        type: 'missing_number',
        rows: missingNumbers.rows
      });
    }

    // Check for duplicate numbers
    const numberMap = new Map<string, number[]>();
    numberColumn.data.forEach((num, index) => {
      const formattedNumber = formatPhoneNumber(String(num || ''));
      if (formattedNumber) {
        const indices = numberMap.get(formattedNumber) || [];
        indices.push(index);
        numberMap.set(formattedNumber, indices);
      }
    });

    let duplicates: any[] = [];

    try {
      duplicates = Array.from(numberMap.entries())
        .filter(([_, indices]) => indices.length > 1)
        .map(([number, indices]) => ({
          type: 'duplicate' as const,
          number,
          rows: indices?.map(index => {
            try {
              const rawName = nameColumn?.data[index];
              const name = typeof rawName === 'string'
                ? rawName.trim()
                : String(rawName || '').trim();

              console.log(`Processing name at index ${index}:`, name);
              return {
                name,
                row: index + 2,
              };
            } catch (e) {
              console.error(`Error processing name at index ${index}:`, e);
              return {
                name: 'ERROR',
                row: index + 2,
              };
            }
          })
        }));
    } catch (error) {
      console.error("Error while processing duplicates:", error);
    }

    // Only add duplicate issues if we're not exporting unique only
    if (!exportUniqueOnly) {

      issues.push(...duplicates);
    }

    // Always check Firestore duplicates
    if (numberColumn) {
      const firestoreDuplicates = await checkFirestoreDuplicates(numberColumn.data);
      // Only add Firestore duplicate issues if we're not exporting unique only
      if (!exportUniqueOnly) {
        issues.push(...firestoreDuplicates);
      } else {
        // Store Firestore duplicates separately for filtering purposes
        setFirestoreDuplicates(firestoreDuplicates);
      }
    }

    setValidationIssues(issues);
  }, [columns, columnMapping.number, columnMapping.name]);

  useEffect(() => {
    if (showExport) {
      checkValidation();
    }
  }, [showExport, checkValidation]);

  const handleExport = async () => {
    if (staffDistribution.length === 0) {
      showToastMessage('Please assign leads to at least one staff member');
      return;
    }

    const totalPercentage = staffDistribution.reduce((sum, dist) => sum + dist.percentage, 0);
    if (totalPercentage !== 100) {
      showToastMessage('Total percentage must equal 100%');
      return;
    }

    if (validationIssues.some(issue => issue.type === 'missing_number' ||
      (issue.type === 'firestore_duplicate' && !exportUniqueOnly))) {
      showToastMessage('Cannot export leads with missing phone numbers' +
        (!exportUniqueOnly ? ' or duplicate phone numbers' : ''));
      return;
    }

    setIsExporting(true);
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) throw new Error('No user ID found');

      // Get companyId from user document
      const userDoc = await getDoc(doc(db, 'users', uid));
      const companyId = userDoc.data()?.companyId;

      if (!companyId) {
        throw new Error('No company ID found');
      }

      const sourceColumn = columns.find(col => col.name === columnMapping.source);
      const campaignColumn = columns.find(col => col.name === columnMapping.campaign);
      if (!sourceColumn) throw new Error('Source column not found');

      // Helper function to sanitize data (convert empty strings to null)
      const sanitizeValue = (value: any, type: string) => {
        if (type === 'name') {
          if (typeof value === 'string') {
            const cleaned = value.replace(/[^A-Za-z\/\\ ]+/g, '').trim();
            return cleaned === '' ? 'NO NAME' : cleaned;
          }
          return 'NO NAME';
        }

        if (type === 'email') {
          // Return the actual email value, or empty string if invalid
          return typeof value === 'string' ? value.trim() : '';
        }

        if (type === 'adminNotes') {
          // Return the actual admin notes value, or empty string if invalid
          return typeof value === 'string' ? value.trim() : '';
        }

        // Default case (optional)
        return '';
      };


      // Calculate total number of leads
      const totalLeads = sourceColumn.data.length;

      // Transform all data
      // Filter out duplicate records when exportUniqueOnly is true
      const exportIndices: number[] = [];

      // Create a set of duplicate indices for filtering
      const duplicateIndices = new Set<number>();
      if (exportUniqueOnly) {
        // Build a map of numbers to their indices
        const numberToIndicesMap = new Map<string, number[]>();

        // Fill the map with all numbers
        const numberColumn = columns.find(col => col.name === columnMapping.number);
        if (numberColumn) {
          numberColumn.data.forEach((num, index) => {
            const formattedNumber = formatPhoneNumber(String(num || ''));
            if (formattedNumber) {
              const indices = numberToIndicesMap.get(formattedNumber) || [];
              indices.push(index);
              numberToIndicesMap.set(formattedNumber, indices);
            }
          });
        }

        // Now mark duplicates - keep first occurrence, exclude the rest
        numberToIndicesMap.forEach((indices) => {
          if (indices.length > 1) {
            // Keep the first index, add all others to the duplicateIndices set
            indices.slice(1).forEach(idx => duplicateIndices.add(idx));
          }
        });

        // Also exclude duplicates from Firestore
        // Use firestoreDuplicates state to ensure we have access to them even if not in validationIssues
        firestoreDuplicates.forEach(issue => {
          if (issue.type === 'firestore_duplicate') {
            // Convert row numbers to indices (0-based)
            const rowIndices = issue.rows.map(r => r.row - 2);
            // Add all indices (exclude all Firestore duplicates)
            rowIndices.forEach(idx => duplicateIndices.add(idx));
          }
        });

        // Check validationIssues as well in case firestoreDuplicates isn't populated
        validationIssues.forEach(issue => {
          if (issue.type === 'firestore_duplicate') {
            // Convert row numbers to indices (0-based)
            const rowIndices = issue.rows.map(r => r.row - 2);
            // Add all indices (exclude all Firestore duplicates)
            rowIndices.forEach(idx => duplicateIndices.add(idx));
          }
        });
      }

      for (let i = 0; i < sourceColumn.data.length; i++) {
        if (!exportUniqueOnly || !duplicateIndices.has(i)) {
          exportIndices.push(i);
        }
      }

      // Get follow-up date column if mapped
      const followUpDateColumn = columnMapping.followUpDate ?
        columns.find(col => col.name === columnMapping.followUpDate) : null;

      // Get created date column if mapped
      const createdAtColumn = columnMapping.createdAt ?
        columns.find(col => col.name === columnMapping.createdAt) : null;

      // Store valid follow-up dates with their row indices for later processing
      const validFollowUps: { index: number; date: Date }[] = [];

      // Store valid created dates with their row indices for later processing
      const validCreatedDates: { index: number; date: Date }[] = [];

      // Helper function to parse Excel dates or regular date strings
      const parseExcelOrRegularDate = (dateValue: any): Date | null => {
        try {
          if (!dateValue) return null;

          // Check if it's an Excel serial date (numeric value)
          const numericValue = Number(String(dateValue));
          if (!isNaN(numericValue) && numericValue > 0) {
            // Convert Excel serial date to JavaScript Date
            return new Date(Math.round((numericValue - (numericValue > 60 ? 2 : 1)) * 86400000) + new Date(1900, 0, 1).getTime());
          } else {
            // Regular date string
            const date = new Date(String(dateValue));
            return !isNaN(date.getTime()) ? date : null;
          }
        } catch (error) {
          return null;
        }
      };

      // Process follow-up dates
      if (followUpDateColumn) {
        exportIndices.forEach(index => {
          const dateValue = followUpDateColumn.data[index];
          if (dateValue && validateFollowUpDate(dateValue)) {
            const date = parseExcelOrRegularDate(dateValue);
            if (date) {
              validFollowUps.push({ index, date });
            }
          }
        });
      }

      // Process created dates
      if (createdAtColumn) {
        exportIndices.forEach(index => {
          const dateValue = createdAtColumn.data[index];
          if (dateValue && validateFollowUpDate(dateValue)) { // Reuse the same validation function
            const date = parseExcelOrRegularDate(dateValue);
            if (date) {
              validCreatedDates.push({ index, date });
            }
          }
        });
      }

      const transformedData: Lead[] = exportIndices.map(index => {
        const nameValue = columns.find(col => col.name === columnMapping.name)?.data[index];
        const name = sanitizeValue(typeof nameValue === 'string' ? nameValue.trim() : nameValue || 'NO NAME', 'name');
        let number = String(columns.find(col => col.name === columnMapping.number)?.data[index] || '');
        const email = sanitizeValue(columns.find(col => col.name === columnMapping.email)?.data[index] || '', 'email');
        const source = sourceColumn.data[index] || '';
        const campaign = campaignColumn?.data[index] || '';

        // Use the consistent formatting function
        number = formatPhoneNumber(number);

        // Create reversed number using consistent logic
        let cleanNumber = number.replace(/\D/g, '');
        let reversedNumber = cleanNumber.split('').reverse().join('');

        // Add + at end of reversed number if longer than 10 chars and doesn't already have +
        if (!reversedNumber.endsWith('+')) {
          reversedNumber = reversedNumber + '+';
        }

        // Check if name consists only of English letters (a-z, A-Z) and spaces
        let uppercaseName;
        try {
          // Trim the name to remove any leading/trailing whitespace
          const trimmedName = name ? name.trim() : '';
          // This regex checks if the name contains only English letters and spaces
          const validNameRegex = /^[a-zA-Z\s\/]+$/;

          if (trimmedName && validNameRegex.test(trimmedName)) {
            // Name is valid, convert to uppercase
            uppercaseName = trimmedName.toUpperCase();
          } else {
            // Name contains non-English letters or other characters
            uppercaseName = "NO NAME";
          }
        } catch (error) {
          // If any error occurs during validation or conversion, use "NO NAME"
          uppercaseName = "NO NAME";
        }

        // Get country and leadStatus values if columns are mapped
        const countryValue = columnMapping.country ? columns.find(col => col.name === columnMapping.country)?.data[index] || '' : '';
        const leadStatusValue = columnMapping.leadStatus ? columns.find(col => col.name === columnMapping.leadStatus)?.data[index] || '' : '';

        return {
          name: uppercaseName,
          number,
          reversedNumber,
          email,
          sourceId: sourceMapping[source] || '',
          createdBy: uid,
          isActive: true,
          campaignId: campaignMapping[campaign] || '',
          assignedTo: getAssignedStaffId(index, totalLeads),
          adminNotes: sanitizeValue(columns.find(col => col.name === columnMapping.adminNotes)?.data[index] || '', 'adminNotes'),
          stageId: stageMapping[columns.find(col => col.name === columnMapping.stage)?.data[index] || ''] || '',
          stateId: stateMapping[columns.find(col => col.name === columnMapping.state)?.data[index] || ''] || '',
          destinationId: countryMapping[countryValue] || '',
          statusId: leadStatusMapping[leadStatusValue] || '',
          hasActiveFollowUp: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
      });

      // Process in chunks of 500 (Firestore batch write limit)
      const chunkSize = 500;
      const chunks = [];
      for (let i = 0; i < transformedData.length; i += chunkSize) {
        chunks.push(transformedData.slice(i, i + chunkSize));
      }

      // Create a map to track which leads need follow-ups
      const leadFollowUpMap = new Map<number, { leadIndex: number; followUpDate: Date }>();

      // Create a map to track which leads have custom created dates
      const leadCreatedDateMap = new Map<number, Date>();

      // Map valid follow-ups to their corresponding lead indices
      validFollowUps.forEach(item => {
        leadFollowUpMap.set(item.index, { leadIndex: item.index, followUpDate: item.date });
      });

      // Map valid created dates to their corresponding lead indices
      validCreatedDates.forEach(item => {
        leadCreatedDateMap.set(item.index, item.date);
      });

      let totalProcessed = 0;
      for (const chunk of chunks) {
        const batch = writeBatch(db);
        const leadsRef = collection(db, 'companies', companyId, 'leads');
        const followUpsRef = collection(db, 'companies', companyId, 'followUps');

        // Map to store lead IDs for follow-up creation
        const leadIds: { [index: number]: string } = {};

        // First pass: Create all leads
        chunk.forEach((lead: Lead, chunkIndex: number) => {
          const newLeadRef = doc(leadsRef);
          const leadId = newLeadRef.id;

          // Store the lead ID with its original index for follow-up creation
          const originalIndex = exportIndices[totalProcessed + chunkIndex];
          leadIds[originalIndex] = leadId;

          // Check if this lead has a custom created date from Excel
          if (leadCreatedDateMap.has(originalIndex)) {
            const customDate = leadCreatedDateMap.get(originalIndex)!;
            // Use the custom date for both createdAt and updatedAt
            lead.createdAt = Timestamp.fromDate(customDate);
            lead.updatedAt = Timestamp.fromDate(customDate);
          }

          // Set the lead without follow-up info first
          batch.set(newLeadRef, lead);
        });

        // Second pass: Create follow-ups and update leads
        chunk.forEach((lead: Lead, chunkIndex: number) => {
          const originalIndex = exportIndices[totalProcessed + chunkIndex];
          const leadId = leadIds[originalIndex];

          // Check if this lead needs a follow-up
          if (leadFollowUpMap.has(originalIndex)) {
            const { followUpDate } = leadFollowUpMap.get(originalIndex)!;

            // Create a new follow-up document
            const newFollowUpRef = doc(followUpsRef);
            const followUpId = newFollowUpRef.id;

            // Get assigned user's name from staffMembers array
            const assignedUser = staffMembers.find(staff => staff.uid === lead.assignedTo);
            const assignedUserName = assignedUser ? assignedUser.name : 'Unknown';

            // Set follow-up data
            batch.set(newFollowUpRef, {
              followUp: 'Follow Up',
              followUpTime: Timestamp.fromDate(followUpDate),
              isDone: false,
              isEdited: false,
              leadId: leadId,
              studentName: lead.name,
              studentNumber: lead.number,
              userId: lead.assignedTo, // Assign to the same staff as the lead
              assignedUserName: assignedUserName,
              createdAt: serverTimestamp(),
              createdBy: uid, // Track who created the follow-up (current user)
              originalAssigneeId: lead.assignedTo, // Store original assignee separately
              originalAssigneeName: assignedUserName
            });

            // Update the lead with follow-up information
            const leadRef = doc(leadsRef, leadId);
            batch.update(leadRef, {
              activeFollowUpId: followUpId,
              hasActiveFollowUp: true
            });
          }
        });

        await batch.commit();
        totalProcessed += chunk.length;
        showToastMessage(`Processed ${totalProcessed} of ${transformedData.length} leads...`);
      }

      const successMessage = exportUniqueOnly && duplicateIndices?.size > 0 ?
        `Successfully exported ${transformedData.length} unique leads! (${duplicateIndices.size} duplicates were skipped)` :
        `Successfully exported ${transformedData.length} leads!`;

      showToastMessage(successMessage);

      // Reset the form
      setFile(null);
      setColumns([]);
      setColumnMapping({
        name: null,
        number: null,
        email: null,
        source: null,
        campaign: null,
        adminNotes: null,
        stage: null,
        state: null,
        country: null,
        leadStatus: null,
        followUpDate: null,
        createdAt: null,
      });
      setSourceMapping({});
      setCampaignMapping({});
      setStageMapping({});
      setStateMapping({});
      setCountryMapping({});
      setLeadStatusMapping({});
      setShowExport(false);
      setStaffDistribution([]);

    } catch (error) {
      console.error('Error exporting leads:', error);
      showToastMessage(`Error exporting leads: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  const renderColumnMapping = () => {
    return (
      <div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-green-800 font-medium">File Successfully Uploaded</h3>
              <p className="text-green-700 text-sm">{file?.name}</p>
            </div>
            <button
              onClick={() => {
                setFile(null);
                setColumns([]);
                setColumnMapping({
                  name: null,
                  number: null,
                  email: null,
                  source: null,
                  campaign: null,
                  adminNotes: null,
                  stage: null,
                  state: null,
                  country: null,
                  leadStatus: null,
                  followUpDate: null,
                  createdAt: null,
                });
              }}
              className="text-green-700 hover:text-green-900"
            >
              Change File
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-medium mb-6">Map Your Excel Columns</h3>

          <div className="space-y-6">
            {Object.entries(REQUIRED_FIELDS).map(([field, config]) => (
              <div key={field} className="border border-gray-100 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <label className="font-medium text-gray-700">
                      {config.label}
                      {config.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <p className="text-sm text-gray-500">{config.description}</p>
                  </div>
                </div>
                <div className="mt-2">
                  <select
                    value={columnMapping[field as keyof ColumnMapping] || ''}
                    onChange={(e) => handleColumnMap(field as keyof ColumnMapping, e.target.value || null)}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a column</option>
                    {getAvailableColumns(field as keyof ColumnMapping).map((column) => (
                      <option key={column.name} value={column.name}>
                        {column.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex justify-end">
            <button
              className={`px-6 py-3 bg-gradient-to-b from-[#FF7B6F] to-[#F9536B] text-white rounded-full flex items-center gap-2 ${!canProceedToNextStep() ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                }`}
              disabled={!canProceedToNextStep()}
              onClick={() => {
                // Validate follow-up dates if a follow-up date column is selected
                const hasFollowUpDateIssues = columnMapping.followUpDate && !validateAllFollowUpDates();
                const hasCreatedDateIssues = columnMapping.createdAt && !validateAllCreatedDates();

                if (hasFollowUpDateIssues && hasCreatedDateIssues) {
                  // Both have issues
                  setInvalidDateType('both');
                  setShowInvalidDatesPopup(true);
                  return;
                } else if (hasFollowUpDateIssues) {
                  // Only follow-up dates have issues
                  setInvalidDateType('followUp');
                  setShowInvalidDatesPopup(true);
                  return;
                } else if (hasCreatedDateIssues) {
                  // Only created dates have issues
                  setInvalidDateType('created');
                  setShowInvalidDatesPopup(true);
                  return;
                }

                setShowRowMapping(true);
                showToastMessage('Moving to row mapping phase...');
              }}
            >
              Continue to Row Mapping
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderRowMapping = () => {
    const renderTabContent = () => {
      switch (activeTab) {
        case 'source':
          return (
            <div>
              <h3 className="text-lg font-medium mb-6">Map Source Values</h3>
              {uniqueSourceValues.map((value) => (
                <div key={value} className="border border-gray-100 p-4 rounded-lg mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <label className="font-medium text-gray-700">
                        Excel Value: <span className="text-blue-600">{value || '(empty)'}</span>
                      </label>
                    </div>
                  </div>
                  <div className="mt-2">
                    <select
                      value={sourceMapping[value] || ''}
                      onChange={(e) => handleSourceMap(value, e.target.value)}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a source</option>
                      {leadSources.map((source) => (
                        <option key={source.id} value={source.id}>
                          {source.source} ({source.channel})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          );
        case 'campaign':
          return (
            <div>
              <h3 className="text-lg font-medium mb-6">Map Campaign Values</h3>
              {uniqueCampaignValues.map((value) => (
                <div key={value} className="border border-gray-100 p-4 rounded-lg mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <label className="font-medium text-gray-700">
                        Excel Value: <span className="text-blue-600">{value || '(empty)'}</span>
                      </label>
                    </div>
                  </div>
                  <div className="mt-2">
                    <select
                      value={campaignMapping[value] || ''}
                      onChange={(e) => handleCampaignMap(value, e.target.value)}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a campaign</option>
                      {leadCampaigns.map((campaign) => (
                        <option key={campaign.id} value={campaign.id}>
                          {campaign.campaign}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          );
        case 'stage':
          return (
            <div>
              <h3 className="text-lg font-medium mb-6">Map Stage Values</h3>
              {uniqueStageValues.map((value) => (
                <div key={value} className="border border-gray-100 p-4 rounded-lg mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <label className="font-medium text-gray-700">
                        Excel Value: <span className="text-blue-600">{value || '(empty)'}</span>
                      </label>
                    </div>
                  </div>
                  <div className="mt-2">
                    <select
                      value={stageMapping[value] || ''}
                      onChange={(e) => handleStageMap(value, e.target.value)}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a stage</option>
                      {leadStages.map((stage) => (
                        <option key={stage.id} value={stage.id}>
                          {stage.stage}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          );
        case 'state':
          return (
            <div>
              <h3 className="text-lg font-medium mb-6">Map State Values</h3>
              {uniqueStateValues.map((value) => (
                <div key={value} className="border border-gray-100 p-4 rounded-lg mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <label className="font-medium text-gray-700">
                        Excel Value: <span className="text-blue-600">{value || '(empty)'}</span>
                      </label>
                    </div>
                  </div>
                  <div className="mt-2">
                    <select
                      value={stateMapping[value] || ''}
                      onChange={(e) => handleStateMap(value, e.target.value)}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a state</option>
                      {leadStates.map((state) => (
                        <option key={state.id} value={state.id}>
                          {state.state}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          );
        case 'country':
          return (
            <div>
              <h3 className="text-lg font-medium mb-6">Map Country Values</h3>
              {uniqueCountryValues.map((value) => (
                <div key={value} className="border border-gray-100 p-4 rounded-lg mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <label className="font-medium text-gray-700">
                        Excel Value: <span className="text-blue-600">{value || '(empty)'}</span>
                      </label>
                    </div>
                  </div>
                  <div className="mt-2">
                    <select
                      value={countryMapping[value] || ''}
                      onChange={(e) => handleCountryMap(value, e.target.value)}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a country</option>
                      {countries.map((country) => (
                        <option key={country.id} value={country.id}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          );
        case 'leadStatus':
          return (
            <div>
              <h3 className="text-lg font-medium mb-6">Map Lead Status Values</h3>
              {uniqueLeadStatusValues.map((value) => (
                <div key={value} className="border border-gray-100 p-4 rounded-lg mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <label className="font-medium text-gray-700">
                        Excel Value: <span className="text-blue-600">{value || '(empty)'}</span>
                      </label>
                    </div>
                  </div>
                  <div className="mt-2">
                    <select
                      value={leadStatusMapping[value] || ''}
                      onChange={(e) => handleLeadStatusMap(value, e.target.value)}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a lead status</option>
                      {leadStatuses.map((status) => (
                        <option key={status.id} value={status.id}>
                          {status.status}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          );
        default:
          return null;
      }
    };

    return (
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <div className="flex space-x-1 mb-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                  px-4 py-2 text-sm font-medium rounded-lg transition-colors
                  ${activeTab === tab.id
                  ? 'bg-gradient-to-r from-[#3483BE] to-[#02385E] text-white'
                  : 'text-gray-500 hover:bg-gray-50'
                }
                `}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="space-y-6">
          {renderTabContent()}
        </div>

        <div className="mt-8 flex justify-end">
          <button
            className={`group relative px-8 py-3 bg-gradient-to-br from-[#FF7B6F] to-[#F9536B] text-white rounded-xl flex items-center gap-3 shadow-lg hover:shadow-xl transition-all duration-200 ${
              // Required mappings
              Object.keys(sourceMapping).length !== uniqueSourceValues.length ||
                Object.keys(campaignMapping).length !== uniqueCampaignValues.length ||
                Object.keys(stageMapping).length !== uniqueStageValues.length ||
                Object.keys(stateMapping).length !== uniqueStateValues.length ||
                // Optional mappings - only check if column is mapped
                (columnMapping.country && Object.keys(countryMapping).length !== uniqueCountryValues.length) ||
                (columnMapping.leadStatus && Object.keys(leadStatusMapping).length !== uniqueLeadStatusValues.length)
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:translate-y-[-1px] active:translate-y-[1px]'
              }`}
            disabled={Boolean(
              // Required mappings
              Object.keys(sourceMapping).length !== uniqueSourceValues.length ||
              Object.keys(campaignMapping).length !== uniqueCampaignValues.length ||
              Object.keys(stageMapping).length !== uniqueStageValues.length ||
              Object.keys(stateMapping).length !== uniqueStateValues.length ||
              // Optional mappings - only check if column is mapped
              (columnMapping.country && Object.keys(countryMapping).length !== uniqueCountryValues.length) ||
              (columnMapping.leadStatus && Object.keys(leadStatusMapping).length !== uniqueLeadStatusValues.length)
            )}
            onClick={handleProceedToExport}
          >
            Continue to Export
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  const calculateLeadDistribution = (totalLeads: number): Map<string, number> => {
    const distribution = new Map<string, number>();
    let remainingLeads = totalLeads;
    let assignedLeads = 0;

    // Sort by percentage descending to handle remainders better
    const sortedDistribution = [...staffDistribution].sort((a, b) => b.percentage - a.percentage);

    // Calculate exact leads for all but the last staff member
    for (let i = 0; i < sortedDistribution.length - 1; i++) {
      const dist = sortedDistribution[i];
      const exactLeads = (dist.percentage / 100) * totalLeads;
      const roundedLeads = Math.floor(exactLeads); // Use floor to avoid over-allocation
      distribution.set(dist.staffId, roundedLeads);
      assignedLeads += roundedLeads;
    }

    // Last staff member gets the remaining leads
    const lastDist = sortedDistribution[sortedDistribution.length - 1];
    if (lastDist) {
      distribution.set(lastDist.staffId, totalLeads - assignedLeads);
    }

    return distribution;
  };

  const getAssignedStaffId = (index: number, totalLeads: number): string => {
    const distribution = calculateLeadDistribution(totalLeads);
    let currentIndex = 0;

    // Convert Map entries to array for iteration
    const entries = Array.from(distribution.entries());
    for (const [staffId, leadCount] of entries) {
      if (index < currentIndex + leadCount) {
        return staffId;
      }
      currentIndex += leadCount;
    }

    // Fallback to the last staff member if something goes wrong
    return staffDistribution[staffDistribution.length - 1].staffId;
  };

  const handleStaffDistributionChange = (staffId: string, percentage: number) => {
    setStaffDistribution(prev => {
      const existing = prev.find(d => d.staffId === staffId);
      if (existing) {
        return prev.map(d => d.staffId === staffId ? { ...d, percentage } : d);
      }
      return [...prev, { staffId, percentage }];
    });
  };

  const handleRemoveStaffDistribution = (staffId: string) => {
    setStaffDistribution(prev => prev.filter(d => d.staffId !== staffId));
  };

  const renderExport = () => {
    const totalPercentage = staffDistribution.reduce((sum, dist) => sum + dist.percentage, 0);
    const remainingPercentage = 100 - totalPercentage;
    const sourceColumn = columns.find(col => col.name === columnMapping.source);

    return (
      <div className="bg-white rounded-2xl p-8 shadow-lg">
        <div className="flex items-center gap-6 mb-10">
          <div className="relative">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg transform transition-all duration-200 hover:scale-105">
              <img
                src={`${process.env.PUBLIC_URL}/images/desktop/sidebaricon/migration-white.svg`}
                alt="Migration Icon"
                className="w-8 h-8"
              />
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-1"
              style={{ fontFamily: 'Montserrat' }}>
              Review & Export
            </h2>
            <p className="text-gray-500">Final step - Assign leads and check for duplicates</p>
          </div>
        </div>

        {/* Phone Number Formatting Disclaimer */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="text-red-800 font-bold">DISCLAIMER: Phone Number Formatting</h3>
              <p className="text-red-700">
                Phone numbers with 10 or fewer digits will be formatted with +91 prefix added to the beginning.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <label className="flex items-center gap-2 text-lg font-medium text-gray-700 mb-1">
                  <span>Distribute Leads</span>
                  <span className="text-red-500 text-sm">*</span>
                </label>
                <p className="text-gray-500 text-sm">Assign leads to staff members by percentage</p>
              </div>
              <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            </div>

            {/* Staff Distribution List */}
            <div className="space-y-4 mb-4">
              {staffDistribution.map((dist) => {
                const staff = staffMembers.find(s => s.uid === dist.staffId);
                return (
                  <div key={dist.staffId} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-grow">
                      <span className="text-sm font-medium text-gray-900">{staff?.name}</span>
                      <span className="text-sm text-gray-500 ml-2">({staff?.email})</span>
                    </div>
                    <div className="w-32">
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={dist.percentage}
                        onChange={(e) => handleStaffDistributionChange(dist.staffId, Number(e.target.value))}
                        className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="flex-shrink-0">
                      <button
                        onClick={() => handleRemoveStaffDistribution(dist.staffId)}
                        className="p-1 text-red-600 hover:text-red-800 rounded-full hover:bg-red-50"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Add New Staff Member */}
            {remainingPercentage > 0 && (
              <div className="relative">
                <select
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white transition-colors duration-200"
                  onChange={(e) => handleStaffDistributionChange(e.target.value, remainingPercentage)}
                  value=""
                >
                  <option value="">Add staff member</option>
                  {staffMembers
                    .filter(staff => !staffDistribution.some(dist => dist.staffId === staff.uid))
                    .map((staff) => (
                      <option key={staff.uid} value={staff.uid}>
                        {staff.name} ({staff.email})
                      </option>
                    ))}
                </select>
              </div>
            )}

            {/* Distribution Summary */}
            <div className="mt-4 bg-blue-50 rounded-lg p-4 mb-4">
              <label className="flex items-center space-x-2 text-blue-700 font-medium cursor-pointer">
                <input
                  type="checkbox"
                  checked={exportUniqueOnly}
                  onChange={() => setExportUniqueOnly(!exportUniqueOnly)}
                  className="h-5 w-5 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                />
                <span>Export unique leads only (exclude duplicates)</span>
              </label>
              {exportUniqueOnly && validationIssues.some(issue => issue.type === 'duplicate' || issue.type === 'firestore_duplicate') && (
                <p className="text-sm text-blue-600 mt-2 ml-7">
                  Only unique leads will be exported. All duplicate entries will be skipped.
                </p>
              )}
            </div>

            <div className="mt-4 p-3 bg-blue-50 rounded-lg space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-blue-700">Total Distribution</span>
                <span className="text-sm font-medium text-blue-700">{totalPercentage}%</span>
              </div>

              {/* Lead count per staff member */}
              {columns.length > 0 && sourceColumn && (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-blue-700 border-t border-blue-200 pt-2">
                    Lead Distribution ({sourceColumn.data.length} total leads)
                  </div>
                  {(() => {
                    const leadDistribution = calculateLeadDistribution(sourceColumn.data.length);
                    return staffDistribution.map((dist) => {
                      const staff = staffMembers.find(s => s.uid === dist.staffId);
                      const leadCount = leadDistribution.get(dist.staffId) || 0;
                      return (
                        <div key={dist.staffId} className="flex justify-between items-center text-sm text-blue-600">
                          <span>{staff?.name}</span>
                          <span>{leadCount} leads ({dist.percentage}%)</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}

              {remainingPercentage > 0 && (
                <div className="text-sm text-blue-600 border-t border-blue-200 pt-2">
                  Remaining: {remainingPercentage}%
                  {columns.length > 0 && sourceColumn && (
                    <span>({Math.round((remainingPercentage / 100) * sourceColumn.data.length)} leads)</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {isCheckingDuplicates && (
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-100 shadow-sm mb-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-blue-700 mb-1">Checking for Duplicate Numbers</h4>
                  <p className="text-blue-600 text-sm">Verifying {duplicateCheckProgress.total} numbers against the database</p>
                </div>
              </div>
              <div className="w-full bg-blue-100 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${(duplicateCheckProgress.checked / duplicateCheckProgress.total) * 100}%` }}
                ></div>
              </div>
              <p className="text-sm text-blue-600 mt-2">
                Checked {duplicateCheckProgress.checked} of {duplicateCheckProgress.total} numbers
              </p>
            </div>
          )}

          {validationIssues.length > 0 && (
            <div className="bg-red-50 rounded-xl p-6 border border-red-100 shadow-sm">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-red-700 mb-1">Data Validation Issues Found</h4>
                  <p className="text-red-600 text-sm">Please fix these issues before exporting</p>
                </div>
              </div>
              <div className="space-y-4">
                {validationIssues.map((issue, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-4 border border-red-100">
                    <p className="font-medium text-red-700 mb-2 flex items-center gap-2">
                      {issue.type === 'duplicate' ? (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          Duplicate Number: {issue.number}
                        </>
                      ) : issue.type === 'firestore_duplicate' ? (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Number already exists in database: {issue.number}
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                          </svg>
                          Missing Phone Numbers
                        </>
                      )}
                    </p>
                    <ul className="ml-6 space-y-2">
                      {issue.rows.map(({ name, row }, rowIdx) => (
                        <li key={rowIdx} className="text-red-600 text-sm flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 text-xs font-medium">
                            {rowIdx + 1}
                          </span>
                          <span>{name}</span>
                          <span className="text-red-400">(Row {row})</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                <div className="mt-4 p-3 bg-red-100 rounded-lg">
                  <p className="text-red-700 text-sm flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Please fix all validation issues in your Excel file before exporting
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-10 flex justify-end">
          <button
            className={`group relative px-8 py-3 bg-gradient-to-br from-[#FF7B6F] to-[#F9536B] text-white rounded-xl flex items-center gap-3 shadow-lg hover:shadow-xl transition-all duration-200 ${totalPercentage !== 100 || isExporting || hasBlockingValidationIssues() || isCheckingDuplicates
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:translate-y-[-1px] active:translate-y-[1px]'
              }`}
            disabled={totalPercentage !== 100 || isExporting || hasBlockingValidationIssues() || isCheckingDuplicates}
            onClick={handleExport}
          >
            {isExporting ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <span>Export Leads</span>
                <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="flex min-h-screen bg-zinc-100">
        <SideNavbar />
        <div className="flex-1">
          <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
            <div className="bg-white p-6 rounded-3xl shadow-md">
              <div className="text-red-500">Error: {error}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen bg-zinc-100">
        <SideNavbar />
        <div className="flex-1">
          <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
            <div className="bg-white p-6 rounded-3xl shadow-md">
              <div className="flex items-center gap-6 mb-8">
                <div className="flex items-center justify-center w-14 h-14 rounded-full bg-gray-200 animate-pulse" />
                <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
              </div>
              <div className="rounded-3xl overflow-hidden shadow-lg bg-zinc-50 p-6">
                <div className="h-6 w-3/4 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-zinc-100">
      <SideNavbar />
      <div className="flex-1">
        <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
          {/* Excel Upload Section */}
          {!file && (
            <div className="bg-white p-6 rounded-3xl shadow-md mb-8">
              <div className="flex items-center gap-6 mb-8">
                <div
                  className="flex items-center justify-center w-14 h-14 rounded-full"
                  style={{ background: 'linear-gradient(180deg, #00A6D9 0%, #3483BE 100%)', }}>
                  <img className='w-8 h-8' src={`${process.env.PUBLIC_URL}/images/desktop/sidebaricon/migration-white.svg`} alt="Migration Icon" />
                </div>
                <h2 className="text-2xl font-medium text-black"
                  style={{ fontFamily: 'Montserrat', fontSize: '24px', fontWeight: '500', lineHeight: '29.26px', textAlign: 'left', }}>
                  Migration Dashboard
                </h2>
              </div>

              {/* Progress Steps for Upload */}
              <div className="mb-8">
                <div className="flex items-center justify-between max-w-4xl mx-auto">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-b from-[#FF7B6F] to-[#F9536B] flex items-center justify-center text-white font-bold">
                      1
                    </div>
                    <span className="mt-2 text-[#F9536B] font-medium">Excel Upload</span>
                  </div>
                  <div className="flex-1 h-1 mx-4 bg-gray-200"></div>
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold">
                      2
                    </div>
                    <span className="mt-2 text-gray-500 font-medium">Map Columns</span>
                  </div>
                  <div className="flex-1 h-1 mx-4 bg-gray-200"></div>
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold">
                      3
                    </div>
                    <span className="mt-2 text-gray-500 font-medium">Map Rows</span>
                  </div>
                  <div className="flex-1 h-1 mx-4 bg-gray-200"></div>
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold">
                      4
                    </div>
                    <span className="mt-2 text-gray-500 font-medium">Review & Import</span>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl overflow-hidden shadow-lg bg-zinc-50 p-6">
                <div className="max-w-4xl mx-auto">
                  <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                      }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="mb-4">
                      <img
                        src={`${process.env.PUBLIC_URL}/images/desktop/excel-icon.svg`}
                        alt="Excel Icon"
                        className="w-20 h-20 mx-auto mb-4"
                      />
                      <h3 className="text-xl font-medium mb-2">Upload Your Excel File</h3>
                      <p className="text-gray-600 mb-6">
                        Start your data migration by uploading your Excel file containing the data you want to import
                      </p>
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="file-upload"
                      />
                      <div className="space-y-4">
                        <label
                          htmlFor="file-upload"
                          className="px-6 py-3 bg-gradient-to-b from-[#FF7B6F] to-[#F9536B] text-white rounded-full cursor-pointer hover:opacity-90 inline-flex items-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                          </svg>
                          Select Excel File
                        </label>
                        <p className="text-sm text-gray-500">
                          or drag and drop your file here
                        </p>
                      </div>
                    </div>
                    <div className="mt-8 p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-medium text-blue-800 mb-2">File Requirements:</h4>
                      <ul className="text-sm text-blue-700 space-y-1 text-left list-disc list-inside">
                        <li>File format: .xlsx or .xls</li>
                        <li>First row should contain column headers</li>
                        <li>Data should start from second row</li>
                        <li>Maximum file size: 10MB</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Column Mapping Section */}
          {file && !showRowMapping && !showExport && (
            <div className="bg-white p-6 rounded-3xl shadow-md">
              <div className="flex items-center gap-6 mb-8">
                <div
                  className="flex items-center justify-center w-14 h-14 rounded-full"
                  style={{ background: 'linear-gradient(180deg, #00A6D9 0%, #3483BE 100%)', }}>
                  <img className='w-8 h-8' src={`${process.env.PUBLIC_URL}/images/desktop/sidebaricon/migration-white.svg`} alt="Migration Icon" />
                </div>
                <h2 className="text-2xl font-medium text-black"
                  style={{ fontFamily: 'Montserrat', fontSize: '24px', fontWeight: '500', lineHeight: '29.26px', textAlign: 'left', }}>
                  Column Mapping
                </h2>
              </div>

              {/* Progress Steps for Mapping */}
              <div className="mb-8">
                <div className="flex items-center justify-between max-w-4xl mx-auto">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold">
                      ✓
                    </div>
                    <span className="mt-2 text-green-600 font-medium">Excel Upload</span>
                  </div>
                  <div className="flex-1 h-1 mx-4 bg-green-500"></div>
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-b from-[#FF7B6F] to-[#F9536B] flex items-center justify-center text-white font-bold">
                      2
                    </div>
                    <span className="mt-2 text-[#F9536B] font-medium">Map Columns</span>
                  </div>
                  <div className="flex-1 h-1 mx-4 bg-gray-200"></div>
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold">
                      3
                    </div>
                    <span className="mt-2 text-gray-500 font-medium">Map Rows</span>
                  </div>
                  <div className="flex-1 h-1 mx-4 bg-gray-200"></div>
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold">
                      4
                    </div>
                    <span className="mt-2 text-gray-500 font-medium">Review & Import</span>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl overflow-hidden shadow-lg bg-zinc-50 p-6">
                <div className="max-w-4xl mx-auto">
                  {renderColumnMapping()}
                </div>
              </div>
            </div>
          )}

          {/* Row Mapping Section */}
          {file && showRowMapping && !showExport && (
            <div className="bg-white p-6 rounded-3xl shadow-md">
              <div className="flex items-center gap-6 mb-8">
                <div
                  className="flex items-center justify-center w-14 h-14 rounded-full"
                  style={{ background: 'linear-gradient(180deg, #00A6D9 0%, #3483BE 100%)', }}>
                  <img className='w-8 h-8' src={`${process.env.PUBLIC_URL}/images/desktop/sidebaricon/migration-white.svg`} alt="Migration Icon" />
                </div>
                <h2 className="text-2xl font-medium text-black"
                  style={{ fontFamily: 'Montserrat', fontSize: '24px', fontWeight: '500', lineHeight: '29.26px', textAlign: 'left', }}>
                  Row Mapping
                </h2>
              </div>

              {/* Progress Steps */}
              <div className="mb-8">
                <div className="flex items-center justify-between max-w-4xl mx-auto">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold">
                      ✓
                    </div>
                    <span className="mt-2 text-green-600 font-medium">Excel Upload</span>
                  </div>
                  <div className="flex-1 h-1 mx-4 bg-green-500"></div>
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold">
                      ✓
                    </div>
                    <span className="mt-2 text-green-600 font-medium">Map Columns</span>
                  </div>
                  <div className="flex-1 h-1 mx-4 bg-gradient-to-r from-green-500 to-[#FF7B6F]"></div>
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-b from-[#FF7B6F] to-[#F9536B] flex items-center justify-center text-white font-bold">
                      3
                    </div>
                    <span className="mt-2 text-[#F9536B] font-medium">Map Rows</span>
                  </div>
                  <div className="flex-1 h-1 mx-4 bg-gray-200"></div>
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold">
                      4
                    </div>
                    <span className="mt-2 text-gray-500 font-medium">Review & Import</span>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl overflow-hidden shadow-lg bg-zinc-50 p-6">
                <div className="max-w-4xl mx-auto">
                  {renderRowMapping()}
                </div>
              </div>
            </div>
          )}

          {/* Export Section */}
          {file && showExport && (
            <div className="bg-white p-6 rounded-3xl shadow-md">
              <div className="flex items-center gap-6 mb-8">
                <div
                  className="flex items-center justify-center w-14 h-14 rounded-full"
                  style={{ background: 'linear-gradient(180deg, #00A6D9 0%, #3483BE 100%)', }}>
                  <img className='w-8 h-8' src={`${process.env.PUBLIC_URL}/images/desktop/sidebaricon/migration-white.svg`} alt="Migration Icon" />
                </div>
                <h2 className="text-2xl font-medium text-black"
                  style={{ fontFamily: 'Montserrat', fontSize: '24px', fontWeight: '500', lineHeight: '29.26px', textAlign: 'left', }}>
                  Review & Export
                </h2>
              </div>

              {/* Progress Steps */}
              <div className="mb-8">
                <div className="flex items-center justify-between max-w-4xl mx-auto">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold">
                      ✓
                    </div>
                    <span className="mt-2 text-green-600 font-medium">Excel Upload</span>
                  </div>
                  <div className="flex-1 h-1 mx-4 bg-green-500"></div>
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold">
                      ✓
                    </div>
                    <span className="mt-2 text-green-600 font-medium">Map Columns</span>
                  </div>
                  <div className="flex-1 h-1 mx-4 bg-green-500"></div>
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold">
                      ✓
                    </div>
                    <span className="mt-2 text-green-600 font-medium">Map Rows</span>
                  </div>
                  <div className="flex-1 h-1 mx-4 bg-gradient-to-r from-green-500 to-[#FF7B6F]"></div>
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-b from-[#FF7B6F] to-[#F9536B] flex items-center justify-center text-white font-bold">
                      4
                    </div>
                    <span className="mt-2 text-[#F9536B] font-medium">Review & Export</span>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl overflow-hidden shadow-lg bg-zinc-50 p-6">
                <div className="max-w-4xl mx-auto">
                  {renderExport()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Invalid Dates Popup */}
      {showInvalidDatesPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-auto">
            <h2 className="text-xl font-bold mb-4 text-red-600">
              {invalidDateType === 'followUp' && 'Invalid Follow-up Dates Detected'}
              {invalidDateType === 'created' && 'Invalid Created Dates Detected'}
              {invalidDateType === 'both' && 'Invalid Follow-up and Created Dates Detected'}
            </h2>

            <p className="mb-4">
              {invalidDateType === 'followUp' && 'The following rows contain follow-up dates that couldn\'t be recognized.'}
              {invalidDateType === 'created' && 'The following rows contain created dates that couldn\'t be recognized.'}
              {invalidDateType === 'both' && 'The following rows contain dates that couldn\'t be recognized.'}
              {' '}Please ensure dates are in a standard format like:
            </p>

            <ul className="list-disc pl-5 mb-4 text-gray-600">
              <li>DD/MM/YYYY or DD-MM-YYYY</li>
              <li>MM/DD/YYYY or MM-DD-YYYY</li>
              <li>YYYY/MM/DD or YYYY-MM-DD</li>
              <li>Month DD, YYYY (e.g., January 21, 2025)</li>
              <li>DD Month YYYY (e.g., 21 January 2025)</li>
            </ul>

            {(invalidDateType === 'followUp' || invalidDateType === 'both') && invalidFollowUpDates.length > 0 && (
              <>
                <h3 className="font-semibold mb-2">Invalid Follow-up Dates:</h3>
                <div className="max-h-60 overflow-y-auto mb-4 border rounded">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="p-2 text-left">Row</th>
                        <th className="p-2 text-left">Invalid Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invalidFollowUpDates.map((item, index) => (
                        <tr key={`followup-${index}`} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                          <td className="p-2">{item.row}</td>
                          <td className="p-2 text-red-500">{item.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {(invalidDateType === 'created' || invalidDateType === 'both') && invalidCreatedDates.length > 0 && (
              <>
                <h3 className="font-semibold mb-2">Invalid Created Dates:</h3>
                <div className="max-h-60 overflow-y-auto mb-4 border rounded">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="p-2 text-left">Row</th>
                        <th className="p-2 text-left">Invalid Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invalidCreatedDates.map((item, index) => (
                        <tr key={`created-${index}`} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                          <td className="p-2">{item.row}</td>
                          <td className="p-2 text-red-500">{item.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <p className="mb-6">
              {invalidDateType === 'followUp' && 'Would you like to proceed without creating follow-ups for these invalid dates?'}
              {invalidDateType === 'created' && 'Would you like to proceed? Invalid created dates will use the current timestamp instead.'}
              {invalidDateType === 'both' && 'Would you like to proceed? Invalid follow-up dates will be ignored and invalid created dates will use the current timestamp.'}
            </p>

            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
                onClick={() => setShowInvalidDatesPopup(false)}
              >
                Go Back
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                onClick={() => {
                  setShowInvalidDatesPopup(false);
                  setShowRowMapping(true);

                  let message = 'Moving to row mapping phase... ';
                  if (invalidDateType === 'followUp') {
                    message += 'Invalid follow-up dates will be ignored.';
                  } else if (invalidDateType === 'created') {
                    message += 'Invalid created dates will use the current timestamp.';
                  } else {
                    message += 'Invalid dates will be handled appropriately.';
                  }

                  showToastMessage(message);
                }}
              >
                Proceed Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {showToast && (
        <Toast
          message={toastMessage}
          onClose={() => setShowToast(false)}
          show={showToast}
        />
      )}
    </div>
  );
};

export default MigrationBeta;